import React, { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  replaceHardwareProjectTrack,
  saveUpdateDescriptionTask,
} from "../../../../store/slices/managerEditTrackingEntries";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import { fetchHardwareProjectTracking } from "../../../../store/slices/hardwareDescriptionSlice";
import { deleteDescriptionTask, fetchDescriptionTasksProjectTracking } from "../../../../store/slices/DescriptionTask";
import ProjectAssignOpTask from "./ProjectAssignOpTask";
import { deleteTask } from "../../../../store/slices/workentriesSlice";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import { isTrainingExpenseDisplayRow } from "../../../../utils/trainingOtherExpenseSync";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";

const ProjectAssignOpHardwre = ({
  row,
  index,
  loggedEmp,
  showActionToAssign,
  setProjectTasksEntries,
  projectTasksEntries,
  projectDescriptionEntries,
  projectHardwareParts,
  handleWorkEntriesSubmit,
  setProjectDescriptionEntries,
  handleDescriptionEntriesSubmit,
  setProjectHardwareParts,
  matchedOldRowDesctiption,
  matchedOldRowWorkEntries,
  matchedRowHardwareEntries,
  setIsLoading,
  isLoading,
  showListState,
  setTripDuration,
  tripDurationInput,
  isViewOnly = false
}) => {
  const sanitizeDecimalInput = (inputValue) => {
    const cleaned = String(inputValue ?? "").replace(/[^0-9.]/g, "");
    const [integerPart, ...decimalParts] = cleaned.split(".");
    if (decimalParts.length === 0) return integerPart;
    return `${integerPart}.${decimalParts.join("")}`;
  };
  const roundHours = (value) => Number((Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2));

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const currency = useCurrencySymbol();
  const { mytrainings } = useSelector((state) => state.myTrainings);

  const matchedPerson = row?.Assignedperson?.find(
    (person) => person?.email === loggedEmp?.EmpEmail
  );

  const [, setHasChanges] = useState(false);
  const [hardwarePartsOp, setHardwarePartsOp] = useState([]);
  const handleTripDuration = (e) => {
    if (isViewOnly) return;
    const value = sanitizeDecimalInput(e.target.value);
    setTripDuration(value)
  }

  useEffect(() => {
    if (projectHardwareParts.length > 0) {
      setHardwarePartsOp(projectHardwareParts)
    } else {
      setHardwarePartsOp([
        {
          assemblepartdescription: "",
          partno: "",
          quantity: "",
          unitprice: "",
          totalprice: "",
        }
      ])
    }
  }, [projectHardwareParts])

  const lockedTrainingRow = useMemo(() => {
    // Show manager/ops-manager assigned training cost as a locked row in OP Other Expenses.
    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const normKey = (v) => String(v || "").trim().toLowerCase();
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const pick = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k];
        if (val != null && String(val).trim() !== "") return val;
      }
      return "";
    };

    if (!row || !loggedEmp || trainingsList.length === 0) return null;

    const assigneeId = resolveRefId(loggedEmp?._id);
    const assigneeEmail = normKey(loggedEmp?.EmpEmail);

    const leadLike = row?.lead || row?.Lead || null;
    const projectIdCandidates = new Set(
      [
        resolveRefId(pick(row, ["_id", "id", "ProjectID", "projectid", "projectId", "ProjectId"])),
        resolveRefId(pick(leadLike, ["_id", "id"])),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const quotationIdCandidates = new Set(
      [
        pick(row, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
        pick(leadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );
    const projectNameCandidates = new Set(
      [pick(row, ["ProjectName", "projectname"]), pick(leadLike, ["ProjectName", "projectname"])]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const leadIdCandidates = new Set(
      [pick(row, ["leadid", "leadId", "LeadId", "LeadID"]), pick(leadLike, ["leadid", "leadId", "LeadId", "LeadID"])]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const matched = trainingsList.filter((t) => {
      const assignedToRaw = t?.assignedto ?? t?.assignedTo;
      const assignedToId = resolveRefId(assignedToRaw);
      const assignedToEmail =
        typeof assignedToRaw === "string" ? normKey(assignedToRaw) : normKey(t?.assignedToEmail ?? "");
      const matchesAssignee =
        (assigneeId && assignedToId && String(assignedToId) === String(assigneeId)) ||
        (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
      if (!matchesAssignee) return false;

      const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
      const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
      const tProjectName = normKey(t?.projectname);
      const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

      return (
        (tProjectId && projectIdCandidates.has(tProjectId)) ||
        (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
        (tProjectName && projectNameCandidates.has(tProjectName)) ||
        (tLeadId && leadIdCandidates.has(tLeadId))
      );
    });

    const normalized = matched.map((t) => {
      const amount = Number(String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, ""));
      const notes = t?.notes ?? t?.note ?? "";
      return {
        amount: Number.isFinite(amount) ? amount : 0,
        notes: String(notes || "").trim(),
      };
    });

    const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
    if (total <= 0 && !joinedNotes) return null;

    return {
      description: "Training Cost",
      value: total,
      notes: joinedNotes,
    };
  }, [mytrainings, row, loggedEmp]);

  const hasTrainingRowInProjectDescriptionEntries = useMemo(
    () =>
      (projectDescriptionEntries || []).some((expenseRow) =>
        isTrainingExpenseDisplayRow(expenseRow)
      ),
    [projectDescriptionEntries]
  );
  const shouldShowLockedTrainingRow =
    !hasTrainingRowInProjectDescriptionEntries && lockedTrainingRow;

  const handleInputChange = (index, field, value) => {
    if (isViewOnly) return;
    setProjectTasksEntries((prevRows) => {
      const newRows = [...prevRows];

      // Validation
      let validatedValue = value;
      if (field === "hoursassigned") {
        validatedValue = sanitizeDecimalInput(value);
      } else if (field === "task" || field === "notes") {
        validatedValue = value.replace(/[^a-zA-Z0-9 .,?'-]/g, "");
      }

      newRows[index] = {
        ...newRows[index],
        [field]: validatedValue,
      };

      // 🔑 Check if at least one row has some data filled
      const hasAnyValue = newRows.some(
        (row) =>
          (row.task && row.task.trim() !== "") ||
          (row.hoursassigned && row.hoursassigned !== "") ||
          (row.notes && row.notes.trim() !== "")
      );

      setHasChanges(hasAnyValue);

      return newRows;
    });
  };

  // const handleAddTaskRow = () => {
  //   if (projectTasksEntries.length < 15) {
  //     setProjectTasksEntries((prev) => [
  //       ...prev,
  //       { task: "", hoursassigned: "", notes: "" },
  //     ]);
  //   }
  // };
  const handleAddTaskRow = () => {
    if (isViewOnly) return;
    if (projectTasksEntries?.length === 0) {
      // If state is empty, add default row
      const newRow = { task: "", hoursassigned: "", notes: "" };
      setProjectTasksEntries([newRow]);
    } else if (projectTasksEntries.length < 15) {
      // Otherwise, add a new row normally
      setProjectTasksEntries([
        ...projectTasksEntries,
        { task: "", hoursassigned: "", notes: "" },
      ]);
    } else {
      // Optional: show a snackbar or message
      // enqueueSnackbar("You can only add up to 15 rows.", { variant: "error" });
    }
  };

  // const handleRemoveTaskRow = (index) => {
  //   setProjectTasksEntries((prev) => prev.filter((_, i) => i !== index));
  // };

  const handleRemoveTaskRow = (index) => {
    if (isViewOnly) return;
    setProjectTasksEntries((prev) => {
      const rowToRemove = prev[index];

      // If it's the last row AND empty, remove it
      if (
        prev.length === 1 &&
        rowToRemove.task === "" &&
        rowToRemove.hoursassigned === "" &&
        rowToRemove.notes === ""
      ) {
        return []; // remove the empty object
      }

      // Otherwise, remove normally
      return prev.filter((_, i) => i !== index);
    });
  };

  // Description Tasks

  const handleTripInputChange = (index, field, value) => {
    if (isViewOnly) return;
    const nextValue =
      field === "value" ? validateAmountValue(value) : value;
    const newRows = [...projectDescriptionEntries];
    newRows[index][field] = nextValue;
    setProjectDescriptionEntries(newRows);
  };

  // const handleAddRow = () => {
  //   if (projectDescriptionEntries.length < 15) {
  //     setProjectDescriptionEntries([
  //       ...projectDescriptionEntries,
  //       { description: "", value: "", notes: "" },
  //     ]);
  //   }
  // };
  const handleAddRow = () => {
    if (isViewOnly) return;
    if (projectDescriptionEntries?.length === 0) {
      // If state is empty, add default row
      const newRow = { description: "", value: "", notes: "" };
      setProjectDescriptionEntries([newRow]);
    } else if (projectDescriptionEntries.length < 15) {
      // Otherwise, add a new row normally
      setProjectDescriptionEntries([
        ...projectDescriptionEntries,
        { description: "", value: "", notes: "" },
      ]);
    } else {
      // Optional: show a snackbar or message
      // enqueueSnackbar("You can only add up to 15 rows.", { variant: "error" });
    }
  };

  // Handle removing a row from triprows
  // const handleRemoveRow = (index) => {
  //   const updatedRows = projectDescriptionEntries.filter((_, i) => i !== index);
  //   setProjectDescriptionEntries(updatedRows);
  // };
  const handleRemoveRow = (index) => {
    if (isViewOnly) return;
    const updatedRows = projectDescriptionEntries?.filter(
      (_, i) => i !== index
    );
    setProjectDescriptionEntries(updatedRows);
  };

  const validateDescriptionOrNotes = (value) => {
    return value.replace(/[^a-zA-Z0-9 .,?'-]/g, "");
  };

  const handleHardwarePartSubmit = async (hardwarePartsOp) => {
    if (isViewOnly) return;
    const cleanedHardwareParts = hardwarePartsOp
      .filter((entry) =>
        entry?.assemblepartdescription !== null &&
        entry?.assemblepartdescription !== undefined &&
        entry?.assemblepartdescription !== "" &&
        entry?.quantity !== null &&
        entry?.quantity !== undefined &&
        entry?.quantity !== ""
      )
      .map((entry) => ({
        _id: entry?._id || null,
        assemblepartdescription: entry.assemblepartdescription,
        partno: entry.partno,
        permonth: entry.permonth,
        quantity: entry.quantity,
        unitprice: entry.unitprice,
        totalprice: entry.totalprice,
        oldtaskid: entry.oldtaskid || null,
      }));

    const Payload = {
      projectid: row._id,
      tasktype: "self",
      tasks: cleanedHardwareParts,
    };

    if (cleanedHardwareParts?.length === 0) {
      setHardwarePartsOp(cleanedHardwareParts)
      return
    };

    setIsLoading(true);
    try {
      await dispatch(replaceHardwareProjectTrack(Payload));
      enqueueSnackbar("Hardware Parts Saved", {
        variant: "success",
      });
      dispatch(fetchAllProjects());
      dispatch(fetchHardwareProjectTracking());
      setIsLoading(false);
    } catch {
      enqueueSnackbar("Failed to save Hardware", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleHardwareDescriptionSubmit = async (projectDescriptionEntries) => {
    if (isViewOnly) return;
    if (
      projectDescriptionEntries.length === 1 &&
      (projectDescriptionEntries[0]?.value === undefined ||
        projectDescriptionEntries[0]?.value === "")
    ) {
      return;
    }

    const cleanedDescriptionEntries = projectDescriptionEntries
      .filter(
        (entry) =>
          entry?.description !== null &&
          entry?.description !== undefined &&
          entry?.description !== "" &&
          entry?.value !== null &&
          entry?.value !== undefined &&
          entry?.value !== "")
      .map(
        (entry) => ({
          _id: entry?._id || null,
          description: entry.description,
          value: parseAmountNumber(entry.value),
          notes: entry.notes,
          oldtaskid: entry.oldtaskid || null,
          tripduration: row?.TypeService?.toLowerCase()?.includes("installation")
            ? (String(tripDurationInput ?? "").trim() !== ""
              ? Number(tripDurationInput)
              : entry.tripduration ?? null)
            : entry.tripduration ?? null,
        })
      );

    const Payload = {
      projectid: row?._id,
      tasktype: "self",
      entries: cleanedDescriptionEntries,
    };

    if (cleanedDescriptionEntries?.length === 0) {
      setProjectDescriptionEntries(cleanedDescriptionEntries)
      return
    };

    setIsLoading(true);
    try {
      await dispatch(saveUpdateDescriptionTask(Payload));
      enqueueSnackbar("Description Entries Saved", {
        variant: "success",
      });
      dispatch(fetchAllProjects());
      dispatch(fetchDescriptionTasksProjectTracking());
      setIsLoading(false);
    } catch {
      enqueueSnackbar("Failed to save Description", {
        variant: "error",
      });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {row?.TypeService?.toLowerCase().includes("hardware")
        // {row.TypeService === "Hardware"
        ? showListState === "tasktable" && (
          <div className="w-full rounded-lg h-full justify-center mb-2">

            <ProjectAssignOpTask row={row}
              hardwarePartsOp={hardwarePartsOp}
              setHardwarePartsOp={setHardwarePartsOp}
              matchedPerson={matchedPerson}
              isViewOnly={isViewOnly}
            />
          </div>
        )
        : showListState === "tasktable" && (
          <>
            <div className="w-full max-h-[14rem] overflow-auto">
              {/* Sticky headers */}
              <div className="w-full sticky top-0 z-10">
                <div className="head flex text-center bg-blue-500 text-white text-[11px]">
                  <div className="w-[10%] border p-1">Sr. No</div>
                  <div className="w-[40%] border p-1">Task</div>
                  <div className="w-[20%] border p-1">Hrs</div>
                  <div className="w-[30%] border p-1">Notes</div>
                </div>
                <div className="head flex text-center bg-white text-black text-[11px]">
                  <div className="w-[10%] border p-1"></div>
                  <div className="w-[40%] border p-1">Total</div>
                  <div className="w-[20%] border p-1">
                    {roundHours(
                      projectTasksEntries?.reduce((sum, item) => {
                        return sum + (parseFloat(item.hoursassigned) || 0);
                      }, 0) || 0
                    )}
                  </div>
                  <div className="w-[30%] border p-1">
                    {/* {projectTasksEntries?.length} Tasks */}
                  </div>
                </div>
              </div>

              <div className="w-full ">
                {projectTasksEntries.map((row, index) => {
                  const matchedOldWorkEntry = matchedOldRowWorkEntries.find(
                    (old) => old._id === row.oldtaskid
                  );
                  return (
                    <React.Fragment key={index}>
                      <div className="flex items-center  text-center w-full text-[11px]">
                        <div className="w-full bg-white">
                          <div className="head flex">
                            <div className={`w-[10%] p-1 border border-gray-200 ${row.lastEditedFields?.includes("task")
                              ? "bg-gray-400 text-white border "
                              : "border border-gray-200 bg-white"
                              }`}>
                              {index + 1}
                            </div>
                            <div
                              className="w-[40%] border border-gray-200"
                              title={
                                matchedOldWorkEntry
                                  ? `Previous Name: ${matchedOldWorkEntry.task}`
                                  : ""
                              }
                            >
                              <input
                                type="text"
                                name="task"
                                value={row.task || ""}
                                disabled={isViewOnly}
                                onChange={(e) => {
                                  const validValue = e.target.value.replace(
                                    /[^a-zA-Z0-9 ]/g,
                                    ""
                                  );
                                  handleInputChange(index, "task", validValue);
                                }}
                                className={`outline-none border-none text-center w-full h-full   p-1  ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes("task")
                                  ? "bg-gray-400 text-white border "
                                  : "border border-gray-200"
                                  }`}
                              />
                            </div>
                            <div
                              className="w-[20%] border border-gray-200"
                              title={
                                matchedOldWorkEntry
                                  ? `Previous Hrs: ${matchedOldWorkEntry.hoursassigned}`
                                  : ""
                              }
                            >
                              <input
                                type="text"
                                name="hoursassigned"
                                value={row.hoursassigned || ""}
                                disabled={isViewOnly}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onChange={(e) => {
                                  const value = sanitizeDecimalInput(e.target.value);

                                  handleInputChange(
                                    index,
                                    "hoursassigned",
                                    value
                                  );
                                }}
                                className={`outline-none border-none text-center w-full h-full p-1  ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes(
                                  "hoursassigned"
                                )
                                  ? "bg-gray-400 text-white border "
                                  : "border border-gray-200 "
                                  }`}
                              />
                            </div>

                            <div
                              className="w-[30%] border border-gray-200"
                              title={
                                matchedOldWorkEntry
                                  ? `Previous Notes: ${matchedOldWorkEntry.notes}`
                                  : ""
                              }
                            >
                              <input
                                type="text"
                                name="notes"
                                value={row.notes || ""}
                                disabled={isViewOnly}
                                onChange={(e) => {
                                  const validValue = e.target.value.replace(
                                    /[^a-zA-Z0-9]/g,
                                    ""
                                  );

                                  handleInputChange(index, "notes", validValue);
                                }}
                                className={`outline-none border-none text-center w-full h-full p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes("notes")
                                  ? "bg-gray-400 text-white border "
                                  : "border border-gray-200 "
                                  }`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {matchedPerson?.projecttracktaskStatus !== "submit" && (
                        <>
                          {matchedPerson?.operationaction === "accept" && (
                            <>
                              {index === projectTasksEntries.length - 1 && (
                                <div className="flex justify-between px-2 pt-1 pb-2">
                                  <button
                                    className={`shadow-xl rounded-md p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-gray-100 hover:bg-gray-200"}`}
                                    onClick={handleAddTaskRow}
                                    disabled={isViewOnly}
                                    title="Add row"
                                  >
                                    <AddIcon fontSize="small" />
                                  </button>
                                  <button
                                    className={`shadow-xl rounded-md p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-gray-100 hover:bg-gray-200"}`}
                                    disabled={isViewOnly}
                                    onClick={() => {
                                      handleRemoveTaskRow(index);
                                      dispatch(deleteTask([row?._id]))
                                    }}
                                    title="Remove row"
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}


                    </React.Fragment>
                  );
                })}

                {projectTasksEntries.length === 0 && (
                  <div className="flex justify-start px-2 pt-1 ">
                    <button
                      className={`shadow-xl rounded-md p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onClick={handleAddTaskRow}
                      disabled={isViewOnly}
                    >
                      <AddIcon fontSize="small" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

      {showListState === "otherexpenses" && (
        <div className="w-full max-h-[14rem] overflow-auto pb-2">
          <div className="w-full sticky top-0 z-10">
            <div className="head flex text-center bg-blue-500 text-white text-[11px]">
              <div className="w-[10%] border p-1">Sr. No</div>
              <div className="w-[40%] border p-1">Description</div>
              <div className="w-[20%] border p-1">Amount ({currency})</div>
              <div className="w-[30%] border p-1">Notes</div>
            </div>
            <div className="head flex text-center bg-white text-black text-[11px]">
              <div className="w-[10%] border p-1"></div>
              <div className="w-[40%] border p-1">Total</div>
              <div className="w-[20%] border p-1">
                {
                  formatAmountComma(
                    (projectDescriptionEntries?.reduce((sum, item) => {
                      return sum + parseAmountNumber(item.value);
                    }, 0) || 0) + parseAmountNumber(shouldShowLockedTrainingRow?.value || 0)
                  )
                }
              </div>
              <div className="w-[30%] border p-1">
                {/* {projectDescriptionEntries?.length} Expenses */}
              </div>
            </div>
            {row?.TypeService?.toLowerCase().includes("installation") && (
              <div className="sticky top-[24px] flex text-center bg-[#1973eb] text-white text-[11px] z-10">
                <div className="w-[10%] border p-1 flex justify-center items-center"></div>
                <div className="w-[40%] border p-1 flex justify-center items-center">Trip Duration</div>
                <div className="w-[20%] border p-1">
                  <input
                    value={tripDurationInput ?? projectDescriptionEntries?.[0]?.tripduration ?? ""}
                    type="text"
                    name="tripduration"
                    placeholder="trip duration"
                    onChange={handleTripDuration}
                    disabled={isViewOnly || matchedPerson?.projecttracktaskStatus === "submit"}
                    className={`outline-none border-none text-center w-full h-full p-0.5 border border-white rounded-md ${
                      isViewOnly || matchedPerson?.projecttracktaskStatus === "submit"
                        ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed"
                        : "text-black"
                    }`}
                  />
                </div>
                <div className="w-[30%] border p-1"></div>
              </div>
            )}
          </div>
          <div className="w-full">
            {projectDescriptionEntries.map((row, index) => {
              const matchedOldDescription = matchedOldRowDesctiption.find(
                (old) => old._id === row.oldtaskid
              );
              return (
                <React.Fragment key={index}>
                  <div className="flex items-center text-center w-full text-[11px]">
                    <div className="head flex w-full">
                      <div className={`w-[10%] p-1  border border-gray-200 ${row.lastEditedFields?.includes("description")
                        ? "bg-gray-400 text-white border "
                        : "bg-white"

                        } `}>
                        {index + 1}
                      </div>
                      <div
                        className="w-[40%] border border-gray-200"
                        title={
                          matchedOldDescription
                            ? `Previous Name:${matchedOldDescription.description}`
                            : ""
                        }
                      >
                        <input
                          type="text"
                          name="description"
                          value={row.description}
                          disabled={isViewOnly}
                          onChange={(e) =>
                            handleTripInputChange(
                              index,
                              "description",
                              validateDescriptionOrNotes(e.target.value)
                            )
                          }
                          className={`outline-none border-none text-center w-full h-full   p-1  ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes("description")
                            ? "bg-gray-400 text-white border "
                            : "bg-white"
                            }`}
                        />
                      </div>

                      <div
                        className="w-[20%] border border-gray-200"
                        title={
                          matchedOldDescription
                            ? `Previous Value: ${matchedOldDescription.value}`
                            : ""
                        }
                      >
                        <input
                          type="text"
                          name="value"
                          value={formatAmountForInput(row.value)}
                          onFocus={(e) => {
                            if (e.target.value === "0") e.target.select();
                          }}
                          onClick={(e) => {
                            if (e.target.value === "0") e.target.select();
                          }}
                          disabled={isViewOnly}
                          onChange={(e) =>
                            handleTripInputChange(
                              index,
                              "value",
                              validateAmountValue(e.target.value)
                            )
                          }
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className={`outline-none border-none text-center w-full h-full   p-1  ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes("value")
                            ? "bg-gray-400 text-white border "
                            : "bg-white"
                            }`}
                        />

                      </div>

                      <div
                        className="w-[30%] border border-gray-200"
                        title={
                          matchedOldDescription
                            ? `Previous Name: ${matchedOldDescription.notes}`
                            : ""
                        }
                      >
                        <input
                          type="text"
                          name="notes"
                          value={row.notes}
                          disabled={isViewOnly}
                          onChange={(e) =>
                            handleTripInputChange(
                              index,
                              "notes",
                              validateDescriptionOrNotes(e.target.value)
                            )
                          }
                          className={`outline-none border-none text-center w-full h-full   p-1  ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : row.lastEditedFields?.includes("notes")
                            ? "bg-gray-400 text-white border "
                            : "bg-white"
                            }`}
                        />
                      </div>
                    </div>
                  </div>

                </React.Fragment>
              );
            })}

            {shouldShowLockedTrainingRow && (
              <div className="flex items-center text-center w-full text-[11px]">
                <div className="head flex w-full">
                  <div className="w-[10%] p-1 border border-gray-200 bg-white">
                    {projectDescriptionEntries.length + 1}
                  </div>
                  <div className="w-[40%] border border-gray-200 bg-white">
                    <input
                      type="text"
                      value={lockedTrainingRow.description}
                      disabled
                      className="outline-none border-none text-center w-full h-full p-1 bg-[#e5e7eb] text-[#111827]"
                    />
                  </div>
                  <div className="w-[20%] border border-gray-200 bg-white">
                    <input
                      type="text"
                      value={formatAmountComma(lockedTrainingRow.value)}
                      disabled
                      className="outline-none border-none text-center w-full h-full p-1 bg-[#e5e7eb] text-[#111827]"
                    />
                  </div>
                  <div className="w-[30%] border border-gray-200 bg-white">
                    <input
                      type="text"
                      value={lockedTrainingRow.notes || ""}
                      disabled
                      className="outline-none border-none text-center w-full h-full p-1 bg-[#e5e7eb] text-[#111827]"
                    />
                  </div>
                </div>
              </div>
            )}

            {matchedPerson?.projecttracktaskStatus !== "submit" &&
              matchedPerson?.operationaction === "accept" && (
                <div className="flex justify-between px-2 pt-1">
                  <button
                    className={`shadow-xl rounded-md p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    onClick={handleAddRow}
                    disabled={isViewOnly || projectDescriptionEntries.length >= 15}
                    title="Add row"
                  >
                    <AddIcon fontSize="small" />
                  </button>
                  <button
                    className={`shadow-xl rounded-md p-1 ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                    disabled={isViewOnly || projectDescriptionEntries.length === 0}
                    onClick={() => {
                      if (projectDescriptionEntries.length === 0) return;
                      handleRemoveRow(projectDescriptionEntries.length - 1);
                      dispatch(deleteDescriptionTask(row?._id));
                    }}
                    title="Remove row"
                  >
                    <RemoveIcon fontSize="small" />
                  </button>
                </div>
              )}
          </div>
        </div>
      )
      }

      {
        row.TypeService !== "Hardware" && (
          <>
            {matchedPerson?.operationaction === "accept" && (
              <div className=" flex justify-end mb-2">
                {matchedPerson?.projecttracktaskStatus === "submit" ? (
                  <div className="mt-2 mr-2 py-1 px-2 inline-block text-orange-600 border rounded-md text-start bg-gray-100">
                    Project submitted & under review
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      handleDescriptionEntriesSubmit(row);
                      handleWorkEntriesSubmit();
                    }}
                    disabled={isViewOnly}
                    className={`mt-2 px-4 py-2 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-blue-500 text-white"}`}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </>
        )
      }

      {
        row?.TypeService?.toLowerCase().includes("hardware") ? (
          // {row.TypeService === "Hardware" ? (
          <div className="flex justify-end mb-2">
            {
              // row.Assignedperson?.every(
              //   (person) => person.projecttracktaskStatus === "submit" )
              matchedPerson?.projecttracktaskStatus === "submit" ? (
                <div className="py-1 px-2 mr-2 inline-block text-orange-600 border rounded-md text-start bg-gray-100">
                  Project submitted & under review
                </div>
              ) : (
                matchedPerson?.operationaction === "accept" && (
                  <button
                    className={`px-2 py-1 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "text-white bg-blue-500 hover:bg-blue-800"}`}
                    disabled={isViewOnly}
                    onClick={() => {
                      handleHardwareDescriptionSubmit(projectDescriptionEntries);
                      handleHardwarePartSubmit(hardwarePartsOp);
                    }}
                  >
                    Submit
                  </button>
                )
              )
            }
          </div>
        ) : (
          <></>
        )
      }
    </div >
  );
};

export default ProjectAssignOpHardwre;
